import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Injectable, signal, viewChild} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {GruppeId, GruppeMedlemListeModell, RedigerUndersøkelseSpesifikasjon, SpørreskjemaModell, SpørreskjemaRessursClient, SpørreundersøkelseId, SpørreundersøkelseModell, SpørreundersøkelseRessursClient} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {lyttTilEsc} from "felles-ng2/støttefunksjoner/tastaturhendelser";
import {SpørreundersøkelseSkjemaKomponent, SpørreundersøkelseSkjemaType} from "../../komponenter/spørreundersøkelse-skjema/spørreundersøkelse-skjema";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {tomSpørreundersøkelseModell} from "felles-ng2/modell/tommeModeller";
import {førsteVerdiLevertFraServer} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatToolbar} from "@angular/material/toolbar";
import {SubmitButtonKomponent} from "web-ng2/delt/komponenter/skjema/submit-button/submit-button";
import {TranslateModule} from "@ngx-translate/core";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {SpørreundersøkelseDatatjeneste} from "felles-ng2/tjenester/datalager/SpørreundersøkelseDatatjeneste";
import {trimTilNull} from "felles-ng2/støttefunksjoner/strenger";

const debug = initDebugger('SpørreundersøkelseRedigerDialogKomponent');


/** Sann dersom bruker oppdaterte undersøkelse, false dersom bruker avbrøt */
export type SpørreundersøkelseRedigerDialogRespons = boolean;

/**
 * Dialog for redigering av undersøkelse
 */
@Component({
    selector: 'spoerreundersoekelse-rediger-dialog',
    templateUrl: 'spørreundersøkelse-rediger-dialog.html',
    styleUrl: 'spørreundersøkelse-rediger-dialog.scss',
    imports: [
        FormsModule,
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatIcon,
        MatIconButton,
        MatToolbar,
        SpørreundersøkelseSkjemaKomponent,
        SubmitButtonKomponent,
        TranslateModule,
        ReactiveFormsModule
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpørreundersøkelseRedigerDialogKomponent {

    static vis(dialog: MatDialog, spørreundersøkelseId: SpørreundersøkelseId): MatDialogRef<SpørreundersøkelseRedigerDialogKomponent, SpørreundersøkelseRedigerDialogRespons> {
        return dialog.open(SpørreundersøkelseRedigerDialogKomponent, {
            data: {spørreundersøkelseId} as DialogParametre,
            disableClose: true,
            ariaLabelledBy: 'spørreundersøkelse-rediger-dialog-tittel',
            height: '75vh', //.mat-mdc-dialog-content er def med 'max-height: 65vh', så mer enn 75vh vil ikke fungere uten at .mat-mdc-dialog-content overstyres
            width: '80vw'
        });
    }

    undersoekelseSkjemaKomp = viewChild.required(SpørreundersøkelseSkjemaKomponent);

    spørreundersøkelseId: SpørreundersøkelseId;
    skjema: SpørreundersøkelseSkjemaType;
    undersoekelse = signal<SpørreundersøkelseModell>(tomSpørreundersøkelseModell());
    tilgjengeligeSkjema = signal<SpørreskjemaModell[]>([]);
    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private avhengigheter: SpørreundersøkelseRedigerDialogKomponentAvhengigheter,
                private dialogRef: MatDialogRef<SpørreundersøkelseRedigerDialogKomponent, SpørreundersøkelseRedigerDialogRespons>,
                destroyRef: DestroyRef) {
        //dialog er konf med disableClose:true for å unngå at klikk utenfor dialog lukker denne, ønsker fortsatt at esc skal lukke
        lyttTilEsc(() => this.avbryt());
        this.spørreundersøkelseId = parametre.spørreundersøkelseId;
        this.skjema = SpørreundersøkelseSkjemaKomponent.opprettSkjema(destroyRef);
        //skjemaer som kan benyttes avhenger av undersøkelsens gruppe, og lastes derfor etter undersøkelsen
        this.populerSkjema().then(() => this.populerTilgjengeligeSkjema());
    }

    async lagre() {
        await this.lagrer.utfør(async () =>
          await this.avhengigheter.lagre(this.spørreundersøkelseId, this.somSpesifikasjon()));
        this.dialogRef.close(true);
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    //private hjelpemetoder

    private async populerSkjema() {
        this.undersoekelse.set(await this.avhengigheter.last(this.spørreundersøkelseId));
        await SpørreundersøkelseSkjemaKomponent.repopulerSkjema(this.skjema, this.undersoekelse(), this.avhengigheter.medlemmer(this.undersoekelse().eiendeGruppeId))
    }

    private async populerTilgjengeligeSkjema() {
        this.tilgjengeligeSkjema.set(await this.avhengigheter.skjemaer(this.undersoekelse().eiendeGruppeId));
    }

    private somSpesifikasjon(): RedigerUndersøkelseSpesifikasjon {
        const verdier = this.skjema.value;
        return {
            tittel: verdier.skjema!.tittel!,
            skjemaId: verdier.skjema!.skjema!.id,
            respondenter: verdier.respondenter!.filter(respondent => respondent.valgt).map(respondent => respondent.medlem!.id),
            antallDagerTilStenging: verdier.aapneForBesvarelse ? verdier.invitasjon!.varighet! : null,
            informerNyeRespondenter: verdier.invitasjon?.sendInvitasjon ?? false,
            beskjedIInvitasjon: trimTilNull(verdier.invitasjon?.beskjed)
        }
    }
}

@Injectable()
export class SpørreundersøkelseRedigerDialogKomponentAvhengigheter {

    constructor(private spørreskjemaRessurs: SpørreskjemaRessursClient, private spørreundersøkelseDatatjeneste: SpørreundersøkelseDatatjeneste, private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste, private oversettelser: Oversettelser) {
    }

    last(id: SpørreundersøkelseId): Promise<SpørreundersøkelseModell> {
        return førsteVerdiLevertFraServer(this.spørreundersøkelseDatatjeneste.hentMedMeta(id));
    }

    medlemmer(gruppeId: GruppeId): Promise<GruppeMedlemListeModell[]> {
        return førsteVerdiLevertFraServer(this.gruppemedlemDatatjeneste.finnTilgjengeligeMedMeta(gruppeId));
    }

    /** Laster skjemaer som kan benyttes i angitt gruppe, ved feil vises feilmelding og tom liste returneres */
    skjemaer(gruppeId: GruppeId): Promise<SpørreskjemaModell[]> {
        return this.spørreskjemaRessurs.finnAlle({gruppeId})
          .somPromiseVisMeldingVedFeilOgReturnerVerdi(
            (feil, oversettelser) => oversettelser.oversett(i18n('Kunne ikke hente tilgjengelige spørreskjemaer')),
            []);
    }

    async lagre(id: SpørreundersøkelseId, spesifikasjon: RedigerUndersøkelseSpesifikasjon): Promise<void> {
        await this.spørreundersøkelseDatatjeneste.oppdaterUndersøkelse(id, spesifikasjon);
    }
}

export interface DialogParametre {
    spørreundersøkelseId: SpørreundersøkelseId;
}
